import React, { useEffect, useRef, useState, useCallback } from 'react';
import QRCode from 'qrcode';
import { QrCodeIcon, DevicePhoneMobileIcon, CameraIcon } from '@heroicons/react/24/outline';

interface QRCodeUploaderProps {
  vehicleId: string;
  isNewVehicle?: boolean;
  onQRCodeGenerated?: (qrCodeUrl: string) => void;
}

const QRCodeUploader: React.FC<QRCodeUploaderProps> = ({
  vehicleId,
  isNewVehicle = false,
  onQRCodeGenerated,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);

  const generateQRCode = useCallback(async () => {
    if (!vehicleId || !canvasRef.current) return;

    setIsGenerating(true);
    try {
      // Generate mobile upload URL - different for new vs existing vehicles
      const baseUrl = window.location.origin;
      const mobileUrl = isNewVehicle
        ? `${baseUrl}/mobile/new-vehicle/${vehicleId}`
        : `${baseUrl}/mobile/vehicle/${vehicleId}`;

      // Generate QR code
      await QRCode.toCanvas(canvasRef.current, mobileUrl, {
        width: 128,
        margin: 2,
        color: {
          dark: '#1f2937',
          light: '#ffffff',
        },
      });

      // Convert canvas to data URL for sharing
      const dataUrl = canvasRef.current.toDataURL();
      setQrCodeUrl(dataUrl);

      if (onQRCodeGenerated) {
        onQRCodeGenerated(dataUrl);
      }
    } catch (error) {
      console.error('Error generating QR code:', error);
    } finally {
      setIsGenerating(false);
    }
  }, [vehicleId, isNewVehicle, onQRCodeGenerated]);

  useEffect(() => {
    if (!vehicleId) return;
    generateQRCode();
  }, [vehicleId, generateQRCode]);

  const handleDownloadQR = () => {
    if (!qrCodeUrl) return;

    const link = document.createElement('a');
    link.download = `vehicle-${vehicleId}-qr.png`;
    link.href = qrCodeUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleShareQR = async () => {
    if (!navigator.share || !qrCodeUrl) return;

    try {
      // Convert data URL to blob
      const response = await fetch(qrCodeUrl);
      const blob = await response.blob();
      const file = new File([blob], `vehicle-${vehicleId}-qr.png`, { type: 'image/png' });

      await navigator.share({
        title: 'Vehicle Upload QR Code',
        text: `Scan to upload images for vehicle ${vehicleId}`,
        files: [file],
      });
    } catch (error) {
      console.error('Error sharing QR code:', error);
      // Fallback to download
      handleDownloadQR();
    }
  };

  return (
    <div
      className={`rounded-lg p-4 border ${
        isNewVehicle
          ? 'bg-gradient-to-br from-green-50 to-emerald-100 border-green-200'
          : 'bg-gradient-to-br from-blue-50 to-indigo-100 border-blue-200'
      }`}
    >
      <div className="flex items-center mb-3">
        <QrCodeIcon
          className={`h-5 w-5 mr-2 ${isNewVehicle ? 'text-green-600' : 'text-blue-600'}`}
        />
        <h5 className={`text-sm font-medium ${isNewVehicle ? 'text-green-900' : 'text-blue-900'}`}>
          {isNewVehicle ? '🚗 New Vehicle Quick Setup' : '📱 Mobile Upload'}
        </h5>
      </div>

      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          {isGenerating ? (
            <div className="w-32 h-32 bg-gray-200 rounded-lg flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="bg-white p-2 rounded-lg shadow-sm border">
              <canvas ref={canvasRef} className="block" style={{ imageRendering: 'pixelated' }} />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="space-y-2 text-sm">
            <div
              className={`flex items-center ${isNewVehicle ? 'text-green-700' : 'text-blue-700'}`}
            >
              <CameraIcon className="h-4 w-4 mr-1" />
              <span className="font-medium">
                {isNewVehicle ? 'Quick Vehicle Setup' : 'VIN Scanner + Photos'}
              </span>
            </div>
            <p
              className={`text-xs leading-relaxed ${isNewVehicle ? 'text-green-600' : 'text-blue-600'}`}
            >
              {isNewVehicle
                ? 'Perfect for lot walks! Scan this QR code to:'
                : 'Scan this QR code with your phone camera to:'}
            </p>
            <ul
              className={`text-xs space-y-1 ml-2 ${isNewVehicle ? 'text-green-600' : 'text-blue-600'}`}
            >
              {isNewVehicle ? (
                <>
                  <li>• 📱 Open on any smartphone</li>
                  <li>• 🔍 Scan VIN with camera (auto-decode)</li>
                  <li>• 📸 Take multiple vehicle photos</li>
                  <li>• ⚡ Auto-populate this form with data</li>
                  <li>• 💾 Save time on data entry</li>
                </>
              ) : (
                <>
                  <li>• Open mobile upload page</li>
                  <li>• Scan VIN with camera</li>
                  <li>• Upload photos directly</li>
                  <li>• Auto-sync with this vehicle</li>
                </>
              )}
            </ul>
          </div>

          <div className="flex gap-2 mt-3">
            <button
              onClick={handleDownloadQR}
              disabled={!qrCodeUrl}
              className="flex-1 btn-secondary text-xs py-1.5 px-2"
            >
              Download QR
            </button>
            {navigator.share !== undefined && (
              <button
                onClick={handleShareQR}
                disabled={!qrCodeUrl}
                className="flex-1 btn-secondary text-xs py-1.5 px-2"
              >
                Share QR
              </button>
            )}
          </div>
        </div>
      </div>

      <div
        className={`mt-3 pt-3 border-t ${isNewVehicle ? 'border-green-200' : 'border-blue-200'}`}
      >
        <div
          className={`flex items-center justify-between text-xs ${isNewVehicle ? 'text-green-600' : 'text-blue-600'}`}
        >
          <div className="flex items-center">
            <DevicePhoneMobileIcon className="h-3 w-3 mr-1" />
            <span>
              {isNewVehicle ? 'Instant lot-to-system workflow' : 'Works on all smartphones'}
            </span>
          </div>
          <span className="font-mono text-[10px] opacity-75">
            {isNewVehicle ? 'TEMP' : 'ID'}: {vehicleId.slice(-6)}
          </span>
        </div>
        {isNewVehicle && (
          <div className="mt-2 p-2 bg-green-100 rounded text-xs text-green-700">
            💡 <strong>Pro Tip:</strong> Print this QR code and keep it with you during lot walks.
            Anyone can scan and add vehicle data instantly!
          </div>
        )}
      </div>
    </div>
  );
};

export default QRCodeUploader;
